import { Button, Modal, Text } from '@shared/components';
import { InlineAutocomplete } from '@shared/components/inline-autocomplete/InlineAutocomplete';
import { InlineQuantifiedField } from '@shared/components/inline-quantified-field/InlineQuantifiedField';
import { InlineTagsAutocomplete } from '@shared/components/inline-tags-autocomplete/InlineTagsAutocomplete';
import { InlineTextArea } from '@shared/components/inline-text-area/InlineTextArea';
import { InlineTextField } from '@shared/components/inline-text-field/InlineTextField';
import {
  getAccessorialOptions,
  setAccessorialsInExtractedData,
} from '@shared/utils/forms/accessorials';

import { RouteForm } from '@shared/components/route-form';
import { useFlowsViewContext } from '@shared/contexts/FlowsViewContext';
import {
  QuoteFormAdaptorContext,
  useQuoteFormAdaptorContext,
} from '@shared/contexts/QuoteFormAdaptorContext';
import {
  GetQuoteResponseMessageDocument,
  JobType,
  QuoteDocument,
  QuoteMileageDocument,
  useRefreshQuoteMileageMutation,
  useUpdateQuoteMutation,
} from '@shared/generated/graphql';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { Quote } from '@shared/types/quote';
import { StyleProps } from '@shared/utils';
import { getDriverRequirementOptions } from '@shared/utils/forms/driverRequirement';
import { getEquipmentTypeOptions } from '@shared/utils/forms/equipment';
import {
  getIsRequiredField,
  mapExtractedDataToQuoteFormValues,
  mutateFieldByPath,
  removeArrayElement,
  setFreightModeInExtractedData,
} from '@shared/utils/forms/formAdaptor';
import { getFreightModeOptions } from '@shared/utils/forms/freightMode';
import { QuoteFormValues } from '@shared/utils/forms/types';
import { getWeightUoMOptions } from '@shared/utils/forms/weight';
import { CompletedFreightOrderTemplate } from 'clerk_common/templates/freight_order/types';
import { StopType } from 'clerk_common/types/stop';
import { format } from 'date-fns';
import { isEqual, isNil, sum } from 'lodash';
import { ReactElement, RefObject, useEffect, useState } from 'react';
import { FormProvider, Path, useForm } from 'react-hook-form';
import { SeeMore } from '../../components/see-more/SeeMore';
import { DistanceDisplay } from './DistanceDisplay';
import { RouteMap } from './RouteMap';

type QuoteDetailsDisplayProps = StyleProps & {
  quote: Quote;
  routeRef?: RefObject<HTMLDivElement | null>;
};

type UpdatedQuoteTrackingProperties = {
  name: string;
  data: {
    field: string;
    value: string;
  };
};

export function QuoteDetailsDisplay(p: QuoteDetailsDisplayProps): ReactElement {
  const formValues = mapExtractedDataToQuoteFormValues(p.quote.extractedData);
  const methods = useForm<QuoteFormValues>({
    defaultValues: {
      ...formValues,
      customFreightTags: p.quote.customFreightTags ?? [],
    },
  });

  const [updateQuote, { loading: updateIsLoading }] = useUpdateQuoteMutation();
  const [refreshMileage] = useRefreshQuoteMileageMutation();
  const { track } = useAnalytics();

  const { setOrganizationId, setOriginatorId, setExtractedData, setJobType } =
    useFlowsViewContext();
  useEffect(() => {
    setOrganizationId(p.quote.organization.id);
    setOriginatorId(p.quote.originator?.id);
    setExtractedData(p.quote.extractedData);
  }, [p.quote.organization.id, p.quote.originator?.id, p.quote.extractedData]);

  setJobType(JobType.QUOTES);
  useEffect(() => {
    methods.reset({
      ...mapExtractedDataToQuoteFormValues(p.quote.extractedData),
      customFreightTags: p.quote.customFreightTags ?? [],
    });
  }, [p.quote.id]);

  const integrationConfigs =
    p.quote.organization.integrationConfigs?.integrations.filter(
      (c) => c.orderType === 'QUOTE'
    );

  const updateExtractedData = async (
    newData: CompletedFreightOrderTemplate,
    trackingProperties?: UpdatedQuoteTrackingProperties
  ) => {
    await updateQuote({
      variables: {
        input: {
          id: p.quote?.id,
          extractedData: newData,
        },
      },
      refetchQueries: [
        { query: QuoteDocument, variables: { id: p.quote?.id } },
        GetQuoteResponseMessageDocument,
        QuoteMileageDocument,
      ],
    });
    track('Updated Quote', { quoteId: p.quote?.id, ...trackingProperties });
    methods.reset({
      ...mapExtractedDataToQuoteFormValues(newData),
      customFreightTags: p.quote.customFreightTags ?? [],
    });
  };

  const updateCustomFreightTags = async (newData: string[]) => {
    await updateQuote({
      variables: {
        input: {
          id: p.quote?.id,
          customFreightTags: newData,
        },
      },
      refetchQueries: [
        { query: QuoteDocument, variables: { id: p.quote?.id } },
        GetQuoteResponseMessageDocument,
        QuoteMileageDocument,
      ],
    });
    track('Updated Quote', { quoteId: p.quote?.id });
    methods.reset({
      ...mapExtractedDataToQuoteFormValues(p.quote.extractedData),
      customFreightTags: newData || [],
    });
  };

  const save = async (
    nameOrNames: Path<QuoteFormValues> | Path<QuoteFormValues>[]
  ) => {
    const names = Array.isArray(nameOrNames) ? nameOrNames : [nameOrNames];
    const tags = p.quote.customFreightTags;

    if (names[0] === 'customFreightTags') {
      const fieldValue = methods.getValues('customFreightTags') || [];
      const tagsIsDifferent = !isEqual(tags, fieldValue);

      tagsIsDifferent && (await updateCustomFreightTags(fieldValue));
      return;
    }

    if (names[0] === 'accessorials') {
      const currentData = p.quote.extractedData;
      if (!currentData) return;
      const fieldValue = methods.getValues('accessorials') || [];
      const newData = setAccessorialsInExtractedData(currentData, fieldValue);
      const dataIsDifferent = !isEqual(currentData, newData);
      dataIsDifferent && (await updateExtractedData(newData));
      return;
    }

    const data = p.quote.extractedData;
    if (!data) return;

    let extractedData: CompletedFreightOrderTemplate = data;
    let freightModeTrackingValue: string | undefined;

    for (const name of names) {
      const fieldValue = methods.getValues(name);

      if (name === 'freightMode' && typeof fieldValue === 'string') {
        if (formValues.freightMode !== fieldValue) {
          freightModeTrackingValue = fieldValue;
        }
        extractedData = setFreightModeInExtractedData(
          extractedData,
          fieldValue
        );
      } else if (typeof fieldValue === 'object' && 'addressOne' in fieldValue) {
        extractedData = mutateFieldByPath(extractedData, name, fieldValue);
      } else if (typeof fieldValue === 'object') {
        const value = format(fieldValue as Date, 'yyyy-MM-dd');
        extractedData = mutateFieldByPath(extractedData, name, value);
      } else if (typeof fieldValue === 'string') {
        extractedData = mutateFieldByPath(extractedData, name, fieldValue);
      }
    }

    const dataIsDifferent = !isEqual(data, extractedData);
    dataIsDifferent &&
      (await updateExtractedData(
        extractedData,
        freightModeTrackingValue === undefined
          ? undefined
          : {
              name: 'freightMode',
              data: {
                field: 'freightMode',
                value: freightModeTrackingValue,
              },
            }
      ));
  };

  const handleRefreshMileage = async () => {
    await refreshMileage({
      variables: {
        input: {
          id: p.quote?.id,
        },
      },
      refetchQueries: [
        { query: QuoteDocument, variables: { id: p.quote.id } },
        { query: QuoteMileageDocument, variables: { id: p.quote.id } },
      ],
    });
    track('Refreshed Quote Mileage', {
      quoteId: p.quote.id,
    });
  };

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setModalIsOpen(false);
    }
  };

  const [removeStopIdx, setRemoveStopIdx] = useState<number | null>(null);

  const promptRemoveStop = (type: StopType, idx: number) => {
    if (type !== 'DELIVERY') {
      console.warn('Removing non-delivery stops is not supported');
      return;
    }

    setRemoveStopIdx(idx);
    setModalIsOpen(true);
  };

  const removeStop = async () => {
    setModalIsOpen(false);
    if (removeStopIdx === null) return;

    const data = p.quote.extractedData;
    if (!data) return;

    const extractedData = removeArrayElement(data, 'stops', removeStopIdx);
    await updateExtractedData(extractedData);
  };

  const weightName: Path<QuoteFormValues> = 'weight.weight';
  const weightUnits: Path<QuoteFormValues> = 'weight.units';
  const isWeightRequired = getIsRequiredField(
    weightName,
    integrationConfigs ?? []
  );
  const equipmentTypeName: Path<QuoteFormValues> = 'equipmentType';
  const customFreightTagsName: Path<QuoteFormValues> = 'customFreightTags';
  const isEquipmentTypeRequired = getIsRequiredField(
    equipmentTypeName,
    integrationConfigs ?? []
  );
  const driverRequirementName: Path<QuoteFormValues> = 'driverRequirement';
  const isDriverRequirementRequired = getIsRequiredField(
    driverRequirementName,
    integrationConfigs ?? []
  );
  const temperatureMinName: Path<QuoteFormValues> = 'temperatureMin';
  const isTemperatureMinRequired = getIsRequiredField(
    temperatureMinName,
    integrationConfigs ?? []
  );
  const temperatureMaxName: Path<QuoteFormValues> = 'temperatureMax';
  const isTemperatureMaxRequired = getIsRequiredField(
    temperatureMaxName,
    integrationConfigs ?? []
  );
  const hasLTLCommodities = 'commodities' in formValues;
  const hasAccessorials = formValues.accessorials !== undefined;
  const commodityName: Path<QuoteFormValues> = 'commodity';
  // TODO(parlato): Use this
  // const commoditiesName: Path<QuoteFormValues> = 'commodities';
  const isCommodityRequired = getIsRequiredField(
    commodityName,
    integrationConfigs ?? []
  );
  const equipmentTypeOptions = getEquipmentTypeOptions(
    p.quote?.organization?.integrationConfigs?.supportedEquipmentTypes
  );
  const driverRequirementOptions = getDriverRequirementOptions();
  const commodityOptions =
    p.quote?.organization?.configurableOptions?.commodities;
  const weightUoMOptions = getWeightUoMOptions();
  const multiStopMileage = sum(p.quote.mileageBetweenStops);
  const customFreightTagsOptions =
    p.quote.organization.settings?.customFreightTags?.map((tag) => ({
      id: tag,
      label: tag,
    })) ?? [];

  return (
    <>
      <FormProvider {...methods}>
        <QuoteFormAdaptorContext.Provider
          value={{
            save,
            loading: updateIsLoading,
            integrationConfigs: integrationConfigs ?? [],
          }}
        >
          <div className="h-fit px-4 pb-[0px] pt-3">
            <RouteForm
              stops={formValues.stops}
              pickups={[formValues.pickup]}
              mileageBetweenStops={p.quote.mileageBetweenStops}
              removeStop={promptRemoveStop}
            />
            {p.routeRef && <div ref={p.routeRef} />}
            <DistanceDisplay
              multiStopMileage={{
                value: multiStopMileage,
                units: 'MI',
              }}
              carrierRate={p.quote.carrierRate}
              onRefresh={handleRefreshMileage}
            />
            <RouteMap googleMapsEmbedUrl={p.quote.googleMapsEmbedUrl} />
          </div>
          <div className="flex flex-col gap-[2px] p-[16px] pt-[8px]">
            <InlineAutocomplete<QuoteFormValues>
              label="Equipment"
              customValidator={(val) => Boolean(val)}
              name={equipmentTypeName}
              required={isEquipmentTypeRequired}
              save={save}
              options={equipmentTypeOptions}
            />
            {!isNil(formValues.temperatureMin) && (
              <InlineTextField<QuoteFormValues>
                label="Min Temp."
                name={temperatureMinName}
                required={isTemperatureMinRequired}
                save={save}
              />
            )}
            {!isNil(formValues.temperatureMax) && (
              <InlineTextField<QuoteFormValues>
                label="Max Temp."
                name={temperatureMaxName}
                required={isTemperatureMaxRequired}
                save={save}
              />
            )}
            {customFreightTagsOptions.length > 0 && (
              <InlineTagsAutocomplete<QuoteFormValues>
                label="Freight Tags"
                name={customFreightTagsName}
                save={save}
                options={customFreightTagsOptions}
                expandable
              />
            )}
            {!isNil(formValues.driverRequirement) && (
              <InlineAutocomplete<QuoteFormValues>
                label="Driver"
                name={driverRequirementName}
                required={isDriverRequirementRequired}
                customValidator={(val) => Boolean(val)}
                save={save}
                options={driverRequirementOptions}
              />
            )}
            {!hasLTLCommodities &&
              (commodityOptions ? (
                <InlineAutocomplete<QuoteFormValues>
                  label="Commodity"
                  name={commodityName}
                  required={isCommodityRequired}
                  customValidator={(val) => Boolean(val)}
                  save={save}
                  options={commodityOptions}
                />
              ) : (
                <InlineTextField<QuoteFormValues>
                  label="Commodity"
                  name={commodityName}
                  required={isCommodityRequired}
                  save={save}
                />
              ))}
            <InlineQuantifiedField<QuoteFormValues>
              label="Weight"
              save={save}
              valueName={weightName}
              uomName={weightUnits}
              required={isWeightRequired}
              uomOptions={weightUoMOptions}
            />
            <MoreDetails save={save} hasAccessorials={hasAccessorials} />
          </div>
        </QuoteFormAdaptorContext.Provider>
      </FormProvider>
      <Modal.Root open={modalIsOpen}>
        <Modal.Content className="p-3" onKeyDown={handleKeyDown}>
          <div className="">
            <Text type="body-sm">
              Are you sure you want to remove this stop?
            </Text>
            <div className="flex justify-end gap-3">
              <Button
                className=""
                onPress={() => setModalIsOpen(false)}
                size="xs"
              >
                Cancel
              </Button>
              <Button
                className=""
                onPress={removeStop}
                size="xs"
                variant="danger"
              >
                Remove
              </Button>
            </div>
          </div>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}

type MoreDetailsProps = {
  save: (name: Path<QuoteFormValues>) => void;
  hasAccessorials: boolean;
};
function MoreDetails(props: MoreDetailsProps) {
  const { save, hasAccessorials } = props;
  const { integrationConfigs } = useQuoteFormAdaptorContext();

  const freightModeName: Path<QuoteFormValues> = 'freightMode';
  const notesName: Path<QuoteFormValues> = 'notes';
  const accessorialsName: Path<QuoteFormValues> = 'accessorials';
  const isFreightModeRequired =
    getIsRequiredField(freightModeName, integrationConfigs) ||
    getIsRequiredField('freightMode.mode', integrationConfigs);
  const isNotesRequired = getIsRequiredField(notesName, integrationConfigs);
  const accessorialOptions = getAccessorialOptions();
  const freightModeOptions = getFreightModeOptions();

  return (
    <SeeMore className="flex w-full">
      <div className="mt-2 flex w-full flex-col gap-1">
        <InlineAutocomplete<QuoteFormValues>
          label="Freight Mode"
          name={freightModeName}
          required={isFreightModeRequired}
          save={save}
          options={freightModeOptions}
        />
        {hasAccessorials && (
          <InlineTagsAutocomplete<QuoteFormValues>
            label="Accessorials"
            name={accessorialsName}
            save={save}
            options={accessorialOptions}
            expandable
          />
        )}
        <InlineTextArea<QuoteFormValues>
          label="Notes"
          name={notesName}
          required={isNotesRequired}
          save={save}
        />
      </div>
    </SeeMore>
  );
}
